import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Timesheet - Manager view for time clock data
 * 
 * Features:
 * - Live view of who's currently clocked in
 * - Timesheet report by date range
 * - Edit/correct entries
 * - Add manual entries
 * - Export functionality (links to payroll export)
 */

const Timesheet = () => {
  const [activeTab, setActiveTab] = useState('live');
  const [liveData, setLiveData] = useState(null);
  const [timesheetData, setTimesheetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Date range for timesheet
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
    
    return {
      start: startOfWeek.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  });

  // Edit modal state
  const [editModal, setEditModal] = useState({ open: false, entry: null });
  const [addModal, setAddModal] = useState({ open: false });

  // Fetch live data
  const fetchLiveData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/clock/admin/live', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLiveData(response.data);
    } catch (err) {
      console.error('Error fetching live data:', err);
      setError('Failed to load live clock data');
    }
  };

  // Fetch timesheet data
  const fetchTimesheetData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/clock/admin/timesheet', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      });
      setTimesheetData(response.data);
    } catch (err) {
      console.error('Error fetching timesheet:', err);
      setError('Failed to load timesheet');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (activeTab === 'live') {
        await fetchLiveData();
      } else {
        await fetchTimesheetData();
      }
      setLoading(false);
    };
    loadData();
  }, [activeTab, dateRange]);

  // Auto-refresh live data every 30 seconds
  useEffect(() => {
    if (activeTab === 'live') {
      const interval = setInterval(fetchLiveData, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Handle edit entry
  const handleEditEntry = async (entryId, updates, reason) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(`/api/clock/admin/${entryId}`, {
        ...updates,
        reason
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEditModal({ open: false, entry: null });
      fetchTimesheetData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update entry');
    }
  };

  // Handle add manual entry
  const handleAddEntry = async (data) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post('/api/clock/admin/add', data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAddModal({ open: false });
      fetchTimesheetData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add entry');
    }
  };

  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return timeStr.slice(0, 5);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Timesheet</h1>
        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'live' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('live')}
          >
            🟢 Live Now
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'timesheet' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('timesheet')}
          >
            📋 Timesheet
          </button>
        </div>
      </div>

      {/* Live Tab */}
      {activeTab === 'live' && (
        <div style={styles.section}>
          <div style={styles.liveHeader}>
            <h2 style={styles.sectionTitle}>Currently Clocked In</h2>
            <div style={styles.liveStats}>
              <span style={styles.liveStat}>
                <span style={styles.liveCount}>{liveData?.clocked_in_count || 0}</span>
                clocked in
              </span>
              {liveData?.not_clocked_in_count > 0 && (
                <span style={styles.liveStatWarning}>
                  {liveData.not_clocked_in_count} scheduled but not clocked in
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : liveData?.employees?.length === 0 ? (
            <div style={styles.empty}>
              <span style={styles.emptyIcon}>🏠</span>
              <p>No one is currently clocked in</p>
            </div>
          ) : (
            <div style={styles.liveGrid}>
              {liveData?.employees?.map((emp) => (
                <div key={emp.entry_id} style={styles.liveCard}>
                  <div style={styles.liveCardHeader}>
                    <div style={styles.employeeInfo}>
                      <div style={styles.employeeName}>{emp.name}</div>
                      <div style={styles.jobRole}>{emp.job_role}</div>
                    </div>
                    <div style={{
                      ...styles.statusBadge,
                      backgroundColor: emp.on_break ? '#fef3c7' : '#dcfce7',
                      color: emp.on_break ? '#b45309' : '#166534'
                    }}>
                      {emp.on_break ? '☕ Break' : '✓ Working'}
                    </div>
                  </div>
                  <div style={styles.liveCardBody}>
                    <div style={styles.liveDetail}>
                      <span style={styles.detailLabel}>Clocked in</span>
                      <span style={styles.detailValue}>
                        {new Date(emp.clock_in).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={styles.liveDetail}>
                      <span style={styles.detailLabel}>Time on shift</span>
                      <span style={styles.detailValue}>{emp.time_on_shift}</span>
                    </div>
                    {emp.scheduled_end && (
                      <div style={styles.liveDetail}>
                        <span style={styles.detailLabel}>Scheduled until</span>
                        <span style={styles.detailValue}>{emp.scheduled_end}</span>
                      </div>
                    )}
                  </div>
                  {emp.late_arrival && (
                    <div style={styles.lateTag}>Late arrival</div>
                  )}
                  {!emp.location_verified && (
                    <div style={styles.locationWarning}>📍 Location not verified</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Timesheet Tab */}
      {activeTab === 'timesheet' && (
        <div style={styles.section}>
          {/* Date Range Selector */}
          <div style={styles.dateRangeBar}>
            <div style={styles.dateInputs}>
              <label style={styles.dateLabel}>
                From:
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  style={styles.dateInput}
                />
              </label>
              <label style={styles.dateLabel}>
                To:
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  style={styles.dateInput}
                />
              </label>
            </div>
            <div style={styles.dateActions}>
              <button
                style={styles.addButton}
                onClick={() => setAddModal({ open: true })}
              >
                + Add Entry
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          {timesheetData && (
            <div style={styles.summaryGrid}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{timesheetData.totals?.entries || 0}</div>
                <div style={styles.summaryLabel}>Total Entries</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{timesheetData.totals?.total_hours?.toFixed(1) || 0}h</div>
                <div style={styles.summaryLabel}>Total Hours</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{timesheetData.totals?.overtime_hours?.toFixed(1) || 0}h</div>
                <div style={styles.summaryLabel}>Overtime</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{timesheetData.totals?.employees || 0}</div>
                <div style={styles.summaryLabel}>Employees</div>
              </div>
            </div>
          )}

          {/* Timesheet Table */}
          {loading ? (
            <div style={styles.loading}>Loading timesheet...</div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Employee</th>
                    <th style={styles.thCenter}>Clock In</th>
                    <th style={styles.thCenter}>Clock Out</th>
                    <th style={styles.thCenter}>Break</th>
                    <th style={styles.thCenter}>Hours</th>
                    <th style={styles.thCenter}>Status</th>
                    <th style={styles.thCenter}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheetData?.entries?.map((entry) => (
                    <tr key={entry.id} style={entry.edited ? styles.editedRow : {}}>
                      <td style={styles.td}>
                        {new Date(entry.date).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.employeeName}>{entry.employee_name}</div>
                        <div style={styles.jobRole}>{entry.job_role}</div>
                      </td>
                      <td style={styles.tdCenter}>{formatTime(entry.clock_in)}</td>
                      <td style={styles.tdCenter}>{formatTime(entry.clock_out)}</td>
                      <td style={styles.tdCenter}>{entry.break_minutes}m</td>
                      <td style={styles.tdCenter}>
                        <span style={styles.hoursValue}>{entry.hours_worked}h</span>
                        {entry.overtime_hours > 0 && (
                          <span style={styles.overtimeBadge}>+{entry.overtime_hours}h OT</span>
                        )}
                      </td>
                      <td style={styles.tdCenter}>
                        <div style={styles.statusTags}>
                          {entry.late && <span style={styles.tagLate}>Late</span>}
                          {entry.early_departure && <span style={styles.tagEarly}>Early</span>}
                          {entry.missed_break && <span style={styles.tagBreak}>No break</span>}
                          {entry.edited && <span style={styles.tagEdited}>Edited</span>}
                          {!entry.location_verified && <span style={styles.tagLocation}>📍</span>}
                        </div>
                      </td>
                      <td style={styles.tdCenter}>
                        <button
                          style={styles.editButton}
                          onClick={() => setEditModal({ open: true, entry })}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {timesheetData?.entries?.length === 0 && (
                <div style={styles.emptyTable}>No entries for selected date range</div>
              )}
            </div>
          )}

          {/* Employee Summary */}
          {timesheetData?.employee_summaries?.length > 0 && (
            <div style={styles.employeeSummarySection}>
              <h3 style={styles.sectionSubtitle}>Employee Summary</h3>
              <div style={styles.employeeSummaryGrid}>
                {timesheetData.employee_summaries.map((emp) => (
                  <div key={emp.employee_id} style={styles.employeeSummaryCard}>
                    <div style={styles.empSummaryName}>{emp.name}</div>
                    <div style={styles.empSummaryStats}>
                      <span>{emp.days_worked} days</span>
                      <span>•</span>
                      <span>{emp.total_hours}h worked</span>
                      {emp.overtime_hours > 0 && (
                        <>
                          <span>•</span>
                          <span style={{ color: '#f59e0b' }}>{emp.overtime_hours}h OT</span>
                        </>
                      )}
                    </div>
                    {emp.late_count > 0 && (
                      <div style={styles.empSummaryLate}>
                        {emp.late_count} late arrival{emp.late_count > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editModal.open && (
        <EditEntryModal
          entry={editModal.entry}
          onSave={handleEditEntry}
          onClose={() => setEditModal({ open: false, entry: null })}
        />
      )}

      {/* Add Modal */}
      {addModal.open && (
        <AddEntryModal
          onSave={handleAddEntry}
          onClose={() => setAddModal({ open: false })}
        />
      )}
    </div>
  );
};

// Edit Entry Modal Component
const EditEntryModal = ({ entry, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    clock_in: entry?.clock_in || '',
    clock_out: entry?.clock_out || '',
    break_minutes: entry?.break_minutes || 0,
    reason: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.reason.trim()) {
      alert('Please provide a reason for the edit');
      return;
    }
    
    const updates = {};
    if (formData.clock_in !== entry?.clock_in) {
      updates.clock_in = `${entry.date}T${formData.clock_in}`;
    }
    if (formData.clock_out && formData.clock_out !== entry?.clock_out) {
      updates.clock_out = `${entry.date}T${formData.clock_out}`;
    }
    if (formData.break_minutes !== entry?.break_minutes) {
      updates.break_minutes = parseInt(formData.break_minutes);
    }
    
    onSave(entry.id, updates, formData.reason);
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>Edit Clock Entry</h2>
        <p style={styles.modalSubtitle}>
          {entry?.employee_name} - {new Date(entry?.date).toLocaleDateString('en-GB')}
        </p>
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Clock In Time</label>
            <input
              type="time"
              value={formData.clock_in}
              onChange={(e) => setFormData(prev => ({ ...prev, clock_in: e.target.value }))}
              style={styles.input}
            />
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Clock Out Time</label>
            <input
              type="time"
              value={formData.clock_out}
              onChange={(e) => setFormData(prev => ({ ...prev, clock_out: e.target.value }))}
              style={styles.input}
            />
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Break (minutes)</label>
            <input
              type="number"
              value={formData.break_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, break_minutes: e.target.value }))}
              style={styles.input}
              min="0"
            />
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Reason for Edit *</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              style={styles.textarea}
              placeholder="e.g., Employee forgot to clock in"
              required
            />
          </div>
          
          <div style={styles.modalButtons}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" style={styles.saveButton}>
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Entry Modal Component
const AddEntryModal = ({ onSave, onClose }) => {
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    clock_in: '09:00',
    clock_out: '17:00',
    break_minutes: 30,
    reason: ''
  });

  useEffect(() => {
    // Fetch employees
    const fetchEmployees = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get('/api/employees', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setEmployees(response.data.employees || response.data || []);
      } catch (err) {
        console.error('Error fetching employees:', err);
      }
    };
    fetchEmployees();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.reason.trim()) {
      alert('Please select an employee and provide a reason');
      return;
    }
    onSave(formData);
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>Add Manual Entry</h2>
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Employee *</label>
            <select
              value={formData.employee_id}
              onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
              style={styles.input}
              required
            >
              <option value="">Select employee...</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name || `${emp.first_name} ${emp.last_name}`}
                </option>
              ))}
            </select>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              style={styles.input}
              required
            />
          </div>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Clock In *</label>
              <input
                type="time"
                value={formData.clock_in}
                onChange={(e) => setFormData(prev => ({ ...prev, clock_in: e.target.value }))}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Clock Out</label>
              <input
                type="time"
                value={formData.clock_out}
                onChange={(e) => setFormData(prev => ({ ...prev, clock_out: e.target.value }))}
                style={styles.input}
              />
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Break (minutes)</label>
            <input
              type="number"
              value={formData.break_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, break_minutes: e.target.value }))}
              style={styles.input}
              min="0"
            />
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Reason *</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              style={styles.textarea}
              placeholder="e.g., Employee forgot to clock in/out"
              required
            />
          </div>
          
          <div style={styles.modalButtons}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" style={styles.saveButton}>
              Add Entry
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    backgroundColor: '#f1f5f9',
    padding: '4px',
    borderRadius: '10px'
  },
  tab: {
    padding: '10px 20px',
    border: 'none',
    background: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    cursor: 'pointer'
  },
  tabActive: {
    backgroundColor: 'white',
    color: '#1e293b',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 16px 0'
  },
  sectionSubtitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '24px 0 12px 0'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#64748b'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#64748b'
  },
  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '12px'
  },
  // Live view styles
  liveHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px'
  },
  liveStats: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  },
  liveStat: {
    fontSize: '14px',
    color: '#64748b'
  },
  liveCount: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#10b981',
    marginRight: '6px'
  },
  liveStatWarning: {
    fontSize: '13px',
    color: '#f59e0b',
    backgroundColor: '#fef3c7',
    padding: '4px 12px',
    borderRadius: '12px'
  },
  liveGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px'
  },
  liveCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    position: 'relative'
  },
  liveCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px'
  },
  employeeInfo: {},
  employeeName: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b'
  },
  jobRole: {
    fontSize: '13px',
    color: '#64748b'
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },
  liveCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  liveDetail: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px'
  },
  detailLabel: {
    color: '#64748b'
  },
  detailValue: {
    fontWeight: '500',
    color: '#334155'
  },
  lateTag: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: '#fef3c7',
    color: '#b45309',
    padding: '2px 8px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: '600'
  },
  locationWarning: {
    marginTop: '8px',
    fontSize: '12px',
    color: '#64748b'
  },
  // Timesheet styles
  dateRangeBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  dateInputs: {
    display: 'flex',
    gap: '16px'
  },
  dateLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#64748b'
  },
  dateInput: {
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px'
  },
  dateActions: {
    display: 'flex',
    gap: '8px'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px'
  },
  summaryCard: {
    backgroundColor: '#f8fafc',
    padding: '16px',
    borderRadius: '10px',
    textAlign: 'center'
  },
  summaryValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b'
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '4px'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #e2e8f0',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase'
  },
  thCenter: {
    textAlign: 'center',
    padding: '12px',
    borderBottom: '2px solid #e2e8f0',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#334155'
  },
  tdCenter: {
    padding: '12px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#334155',
    textAlign: 'center'
  },
  editedRow: {
    backgroundColor: '#fffbeb'
  },
  hoursValue: {
    fontWeight: '600'
  },
  overtimeBadge: {
    marginLeft: '6px',
    backgroundColor: '#fef3c7',
    color: '#b45309',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '11px'
  },
  statusTags: {
    display: 'flex',
    gap: '4px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  tagLate: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '11px'
  },
  tagEarly: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '11px'
  },
  tagBreak: {
    backgroundColor: '#e0e7ff',
    color: '#4338ca',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '11px'
  },
  tagEdited: {
    backgroundColor: '#f3e8ff',
    color: '#7c3aed',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '11px'
  },
  tagLocation: {
    fontSize: '12px'
  },
  editButton: {
    padding: '6px 12px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer'
  },
  emptyTable: {
    textAlign: 'center',
    padding: '40px',
    color: '#64748b'
  },
  employeeSummarySection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e2e8f0'
  },
  employeeSummaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '12px'
  },
  employeeSummaryCard: {
    backgroundColor: '#f8fafc',
    padding: '14px',
    borderRadius: '10px'
  },
  empSummaryName: {
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px'
  },
  empSummaryStats: {
    fontSize: '13px',
    color: '#64748b',
    display: 'flex',
    gap: '6px'
  },
  empSummaryLate: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#f59e0b'
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '24px',
    width: '90%',
    maxWidth: '450px',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  modalSubtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0 0 20px 0'
  },
  formGroup: {
    marginBottom: '16px'
  },
  formRow: {
    display: 'flex',
    gap: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    minHeight: '80px',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  saveButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

export default Timesheet;